import { AxiosResponse } from 'axios';
import { takeLatest, takeEvery, all, put, call } from 'redux-saga/effects';
import { IRequestParams, request } from 'helpers/request';
import { logSagaErrorMessage, CONSTANTS } from 'utils';
import {
	finishSurvey,
	getFinishModule,
	getModuleEvaluation,
	getModuleGeneralInfo,
	getModuleRatings,
	getRespondentData,
	postSurveyData,
	setActiveModule,
} from './survey.actions';
import {
	IRespondentData,
	ISurveyDataPayload,
	IRatingsModule,
	IEvaluationModule,
	IGeneralInfoModule,
} from './models';
import * as types from './ActionTypes';

const surveyBaseUrl = `${CONSTANTS.baseApiPath}${CONSTANTS.moduleSurveyPath}/respondent`;

const mapFinishModuleUrl = {
	1: 'finishFirstModule',
	2: 'finishSecondModule',
	3: 'finish',
};

function* getFinishModuleSaga({
	payload: { id, activeModule },
}: {
	payload: {
		id: string;
		activeModule: keyof typeof mapFinishModuleUrl;
	};
}) {
	try {
		yield put(getFinishModule.loading());

		const getFinishModuleParams = {
			url: `${surveyBaseUrl}/${id}/${mapFinishModuleUrl[activeModule]}`,
		};

		const { data } = yield call(request, getFinishModuleParams);

		if (activeModule === 3) {
			yield put(finishSurvey(data));
		} else {
			yield put(setActiveModule(activeModule + 1));
			yield put(getRespondentData.default({ id }));
		}
	} catch (e) {
		logSagaErrorMessage('getFinishModule', e);
		yield put(getFinishModule.fail(e));
	}
}

function* getSurveyDataSaga({ payload }: { payload: { id: string } }) {
	try {
		yield put(getRespondentData.loading());

		const getSurveyDataParams = {
			url: `${surveyBaseUrl}/${payload.id}/start`,
		};

		const { data }: AxiosResponse<IRespondentData> = yield call(
			request,
			getSurveyDataParams,
		);

		yield put(getRespondentData.success(data));
	} catch (e) {
		logSagaErrorMessage('getSurveyData', e);
		yield put(getRespondentData.fail(e));
	}
}

function* getGeneralInfoModuleSaga({
	payload,
}: {
	payload: {
		id: string;
	};
}) {
	try {
		yield put(getModuleGeneralInfo.loading());

		const getSurveyDataParams = {
			url: `${surveyBaseUrl}/${payload.id}/firstModule`,
		};

		const { data }: AxiosResponse<IGeneralInfoModule> = yield call(
			request,
			getSurveyDataParams,
		);

		yield put(getModuleGeneralInfo.success(data));
	} catch (e) {
		logSagaErrorMessage('getModuleGeneralInfo', e);
		yield put(getModuleGeneralInfo.fail(e));
	}
}

function* getRatingsModuleSaga({ payload }: { payload: { id: string } }) {
	try {
		yield put(getModuleRatings.loading());

		const getSurveyDataParams = {
			url: `${surveyBaseUrl}/${payload.id}/secondModule`,
		};

		const { data }: AxiosResponse<IRatingsModule> = yield call(
			request,
			getSurveyDataParams,
		);

		yield put(getModuleRatings.success(data));
	} catch (e) {
		logSagaErrorMessage('getModuleRatings', e);
		yield put(getModuleRatings.fail(e));
	}
}

function* getEvaluationModuleSaga({ payload }: { payload: { id: string } }) {
	try {
		yield put(getModuleEvaluation.loading());

		const getSurveyDataParams = {
			url: `${surveyBaseUrl}/${payload.id}/thirdModule`,
		};

		const { data }: AxiosResponse<IEvaluationModule> = yield call(
			request,
			getSurveyDataParams,
		);

		yield put(getModuleEvaluation.success(data));
	} catch (e) {
		logSagaErrorMessage('getModuleEvaluation', e);
		yield put(getModuleEvaluation.fail(e));
	}
}

function* postSurveyDataSaga({
	payload: { data, id, module, quiet, shouldFinish },
}: {
	payload: ISurveyDataPayload;
}) {
	try {
		if (!quiet) {
			yield put(postSurveyData.loading());
		}

		const postSurveyDataParams: IRequestParams = {
			method: 'POST',
			url: `${surveyBaseUrl}/${id}/answer`,
			data,
		};

		yield call(request, postSurveyDataParams);

		if (shouldFinish) {
			yield call(getFinishModuleSaga, {
				payload: {
					id,
					activeModule: module as keyof typeof mapFinishModuleUrl,
				},
			});
		}

		if (!quiet) {
			yield put(postSurveyData.success());
		}
	} catch (e) {
		logSagaErrorMessage('postSurveyData', e);
		yield put(postSurveyData.fail(e));
	}
}

export default function* surveySaga() {
	yield all([
		// @ts-ignore
		takeEvery(types.GET_RESPONDENT_DATA.DEFAULT, getSurveyDataSaga),
		// @ts-ignore
		takeEvery(types.GET_MODULE_GENERAL_INFO.DEFAULT, getGeneralInfoModuleSaga),
		// @ts-ignore
		takeEvery(types.GET_MODULE_RATINGS.DEFAULT, getRatingsModuleSaga),
		// @ts-ignore
		takeEvery(types.GET_MODULE_EVALUATION.DEFAULT, getEvaluationModuleSaga),
		// @ts-ignore
		takeLatest(types.POST_SURVEY_DATA.DEFAULT, postSurveyDataSaga),
		// @ts-ignore
		takeEvery(types.GET_FINISH_MODULE.DEFAULT, getFinishModuleSaga),
	]);
}
